import { InputAmount } from "@kelp-dao/kernel-design-system";
import { CoinOption } from "@kelp-dao/kernel-design-system/dist/components/text-fields/InputAmount";
import React, { ReactNode, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Flex, Text } from "@chakra-ui/react";
import BaseImage from "@components/BaseImage";
import { TOKEN_SELECTION } from "@constants/analytics";
import {
  LSTIconMapping,
  LstOptions,
  getLSTOptionsForChain,
} from "@constants/common";
import useInstantWithdrawalAvailability from "@hooks/useInstantWithdrawalAvailability";
import useDappChain from "@hooks/useDappChain";
import useDappConfig from "@hooks/useDappConfig";
import useUserType from "@hooks/useUserType";
import { updateUserData } from "@store";
import { getUrlForLocalImage, trackEvent } from "utils";
import { EUnstakeMode, TUnstakeMode } from "./restake/types";

export interface TokenInputProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
  hideTags?: boolean;
  hideETH?: boolean;
  forUnstake?: boolean;
  title?: string;
  isVaultPage?: boolean;
  isYieldVaultPage?: boolean;
  rightSection?: ReactNode;
  placeholder?: string;
  className?: string;
  showBalance?: boolean;
  onMaxClick?: () => void;
  balance?: string;
  tokenBalance?: string;
  token?: string;
  selectedMode?: TUnstakeMode;
  handleModeChange?: (mode: TUnstakeMode) => void;
  explicitLabel?: string;
  explicitBalance?: string;
}

const TokenInput: React.FC<TokenInputProps> = ({
  value,
  onChange,
  error,
  disabled = false,
  hideTags = false,
  hideETH = false,
  forUnstake = false,
  isVaultPage = false,
  isYieldVaultPage = false,
  placeholder = "0",
  className = "",
  showBalance = true,
  // balance,
  tokenBalance,
  onMaxClick,
  // token,
  selectedMode,
  handleModeChange,
  explicitLabel,
  explicitBalance,
}) => {
  const { selectedERC20, selectedERC20Vault, selectedERC20YieldVault } =
    useSelector((state: any) => state.user);

  //   const { instantWithdrawalAvailable } = useInstantWithdrawalAvailability();
  const { chain } = useDappChain();
  const dispatch = useDispatch();
  const { config } = useDappConfig();
  const { isStaderLedgerUser } = useUserType();
  const { instantWithdrawalAvailable } = useInstantWithdrawalAvailability();

  // Get token options
  const options = getLSTOptionsForChain(
    chain.id,
    hideETH || (!forUnstake && !config.nativeToken.isRestakeSupported),
    isStaderLedgerUser,
    isVaultPage
  );

  const sortKey = forUnstake ? "unstakePriority" : "priority";
  const sortedOptions = options.sort(
    (a: any, b: any) => (a?.[sortKey] || 0) - (b?.[sortKey] || 0)
  );

  // Get selected asset
  const getSelectedAsset = () => {
    if (isVaultPage) return selectedERC20Vault;
    if (isYieldVaultPage) return selectedERC20YieldVault;
    return selectedERC20;
  };

  const selectedAsset = getSelectedAsset();

  // Get Redux key for storing selection
  const getKey = () =>
    (isVaultPage && "selectedERC20Vault") ||
    (isYieldVaultPage && "selectedERC20YieldVault") ||
    "selectedERC20";

  // Convert LstOptions to CoinOption format for kernel design system
  // add right element to the coinOptions only for unstake mode & dont use null, just dont add rightElement prop itself
  const coinOptions: CoinOption[] = sortedOptions
    .filter((option) => option.key !== "divider" && option.key !== "other")
    .map((option: LstOptions) => ({
      id: option.key,
      label: option.title || option.key,
      iconSrc: getUrlForLocalImage(LSTIconMapping[option.key]) || "",
      isDisabled: option.isDisabled || false,
      tags: hideTags ? [] : option.tags || [],
    }));

  // Memoize the selected coin to ensure stable reference
  const selectedCoin = useMemo(() => {
    return coinOptions.find((coin) => coin.id === selectedAsset);
  }, [coinOptions, selectedAsset]);

  const bufferPoolForAsset =
    selectedAsset &&
    !isNaN(Number(instantWithdrawalAvailable[selectedAsset])) &&
    Number(instantWithdrawalAvailable[selectedAsset]);

  // Track whether user has manually selected a mode
  const hasManuallySelectedMode = useRef(false);

  // Reset manual selection flag when selectedCoin changes
  useEffect(() => {
    hasManuallySelectedMode.current = false;
  }, [selectedCoin?.id]);

  // Only auto-change mode if user hasn't manually selected one
  useEffect(() => {
    if (hasManuallySelectedMode.current) return;

    if (bufferPoolForAsset > 0 && selectedMode !== EUnstakeMode.INSTANT) {
      handleModeChange?.(EUnstakeMode.INSTANT);
    } else if (
      bufferPoolForAsset <= 0 &&
      selectedMode !== EUnstakeMode.STANDARD
    ) {
      handleModeChange?.(EUnstakeMode.STANDARD);
    }
  }, [selectedCoin]);

  // Track when mode changes externally (manually by user)
  useEffect(() => {
    if (selectedMode !== undefined) {
      hasManuallySelectedMode.current = true;
    }
  }, [selectedMode]);

  // Handle token selection
  const handleTokenSelect = (coin: CoinOption) => {
    dispatch(updateUserData({ [getKey()]: coin.id }));
    trackEvent(TOKEN_SELECTION, { token: coin.id });
  };

  // Handle max click - get max available balance
  const handleMaxClick = () => {
    if (onMaxClick) {
      onMaxClick();
    }
  };

  // Create a stable key that changes when the important data changes
  const inputKey = useMemo(() => {
    return `${forUnstake ? "unstake" : "stake"}-${selectedAsset}-${chain.id}`;
  }, [forUnstake, selectedAsset, chain.id]);

  return (
    <div className={`w-full ${className}`}>
      {/* Input Amount Component */}
      <InputAmount
        key={inputKey}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        error={!!error}
        disabled={disabled}
        coins={coinOptions}
        coinSelected={selectedCoin}
        onCoinSelect={handleTokenSelect}
        rightButton={{
          show: true,
          text: "Max",
          disabled: !showBalance,
          onClick: handleMaxClick,
        }}
        className="w-full"
        showBalance={showBalance}
        coinBalance={tokenBalance}
        dropdownWidth={220}
        explicitLabel={explicitLabel ? explicitLabel : undefined}
        explicitBalance={explicitBalance ? explicitBalance : undefined}
      />

      {error && (
        <Flex alignItems="center" mt="8px">
          <BaseImage
            src="/assets/transactions/error.svg"
            width={20}
            height={20}
            alt="error-icon"
          />
          <Text ml="0.5rem" color="error" fontSize="12px" fontWeight="400">
            {error ? error : "error"}
          </Text>
        </Flex>
      )}

      {/* Additional balance display for unstake mode */}
      {/* {forUnstake && selectedCoin && (
        <div className="flex justify-between items-center mt-2 px-1">
          <Typography
            type="body"
            variant="small"
            className="text-text-secondary"
          >
            Available to unstake
          </Typography>
          <Typography type="body" variant="small" className="text-text-primary">
            {selectedCoin.balance} {token || selectedCoin.id}
          </Typography>
        </div>
      )} */}
    </div>
  );
};

export default TokenInput;
